import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import OpsLayout from '../components/OpsLayout';
import { API_BASE_URL } from '../config/config';
import opsStatusTone from '../utils/opsStatusTone';
import '../css/PackagePurchaseManagement.css';

// spec D5: 아직 구현되지 않은 기능이다. 버튼을 지우지 않고 비활성 + "준비 중" 배지로 남긴다.
const Ready = () => <span className="ops-page__ready">준비 중</span>;

// 사용 기간 표시. 시작·종료 둘 다 없으면 "—"(데이터 없음), 하나만 있어도 있는 값을 보여준다.
const formatUsagePeriod = (start, end) => {
    if (!start && !end) return '—';
    return `${start || ''} ~ ${end || ''}`;
};

const PackagePurchaseManagement = () => {
    // SUPER_ADMIN 권한 확인
    const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

    // 모달 상태
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 신규 사용자 입력 데이터 상태
    const [newUserData, setNewUserData] = useState({
        orderNumber: '',
        orderDate: new Date().toISOString().split('T')[0],
        refundDate: '',
        paymentStatus: '',
        recipient: '',
        recipientContact: '',
        packageType: '',
        quantity: '',
        usageStartDate: '',
        smsRecipient: '',
        smsContact: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        remarks: ''
    });

    // 사용자 등록 폼 상태
    const [userForms, setUserForms] = useState([
        {
            id: 1,
            name: '',
            birthDate: '',
            phone: '',
            orderHistory: null,
            hasSearched: false
        }
    ]);

    // 검색 조건 상태
    const [searchConditions, setSearchConditions] = useState({
        packageType: '',
        paymentStatus: '',
        userRegistrationStatus: '',
        orderDateStart: '',
        orderDateEnd: '',
        recipient: '',
        recipientContact: '',
        userName: '',
        userContact: ''
    });

    // 결제 이력 데이터 상태
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [paymentHistoryTotal, setPaymentHistoryTotal] = useState(0);
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
    const [paymentHistoryPage, setPaymentHistoryPage] = useState(1);
    const [paymentHistoryTotalPages, setPaymentHistoryTotalPages] = useState(1);

    // 공통 상태
    const [itemsPerPage] = useState(10);

    // 패키지 종류 옵션
    const packageTypes = [
        '효도1',
        '효도2',
        '개인관리',
        '효도1-정기6',
        '효도1-정기12',
        '효도1-무제한',
        '효도2-정기6',
        '효도2-정기12',
        '효도2-무제한'
    ];

    // 결제 상태 옵션
    const paymentStatuses = [
        '결제',
        '환불요청',
        '환불'
    ];

    // 사용자 등록 상태 옵션
    const userRegistrationStatuses = [
        '미완료',
        '완료'
    ];

    // 조회 조건 세그먼트. 값·상태 이름은 기존 <select> 의 것을 그대로 쓴다("전체" 는 '' 다).
    const paymentStatusFilters = [['', '전체'], ...paymentStatuses.map((status) => [status, status])];
    const userRegistrationStatusFilters = [['', '전체'], ...userRegistrationStatuses.map((status) => [status, status])];

    useEffect(() => {
        if (!isSuperAdmin) {
            window.alert('접근 권한이 없습니다.');
            window.history.back();
            return;
        }

        // 기본 검색 조건 설정 (최근 1개월)
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);

        setSearchConditions(prev => ({
            ...prev,
            orderDateStart: oneMonthAgo.toISOString().split('T')[0],
            orderDateEnd: today.toISOString().split('T')[0]
        }));
    }, [isSuperAdmin]);

    // 검색 조건 변경 핸들러
    const handleSearchConditionChange = (field, value) => {
        setSearchConditions(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 신규 사용자 데이터 변경 핸들러
    const handleNewUserDataChange = (field, value) => {
        setNewUserData(prev => {
            const updated = { ...prev, [field]: value };

            // 패키지 종류나 시작일자가 변경되면 종료일자 자동 계산
            if (field === 'packageType' || field === 'usageStartDate') {
                updated.endDate = getEndDate(updated.packageType, updated.usageStartDate);
            }

            return updated;
        });
    };

    // 패키지 종류에 따른 사용기한 계산
    const getUsagePeriod = (packageType) => {
        const periodMap = {
            '효도1': '3개월',
            '효도2': '3개월',
            '개인관리': '무제한',
            '효도1-정기6': '6개월',
            '효도1-정기12': '12개월',
            '효도1-무제한': '무제한',
            '효도2-정기6': '6개월',
            '효도2-정기12': '12개월',
            '효도2-무제한': '무제한'
        };
        return periodMap[packageType] || '';
    };

    // 종료일자 계산
    const getEndDate = (packageType, startDate) => {
        if (!packageType || !startDate) return '';

        // 무제한 패키지들 처리
        if (packageType.includes('무제한') || packageType === '개인관리') {
            return '9999-12-31';
        }

        const start = new Date(startDate);
        let months = 3; // 기본값

        if (packageType.includes('정기6')) months = 6;
        else if (packageType.includes('정기12')) months = 12;

        const end = new Date(start);
        end.setMonth(end.getMonth() + months);
        end.setDate(end.getDate() - 1); // 마지막 날짜

        return end.toISOString().split('T')[0];
    };

    // 결제 이력 조회 함수
    const fetchPaymentHistory = async () => {
        setPaymentHistoryLoading(true);
        try {
            const token = localStorage.getItem('token');
            console.log('API 호출 파라미터:', {
                page: paymentHistoryPage,
                limit: itemsPerPage,
                ...searchConditions
            });

            const response = await axios.get(`${API_BASE_URL}/api/package-purchase/history`, {
                params: {
                    page: paymentHistoryPage,
                    limit: itemsPerPage,
                    ...searchConditions
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('API 응답 전체:', response);
            console.log('API 응답 데이터:', response.data);

            if (response.data.success) {
                setPaymentHistory(response.data.data?.history || []);
                setPaymentHistoryTotal(response.data.data?.total || 0);
                setPaymentHistoryTotalPages(Math.ceil((response.data.data?.total || 0) / itemsPerPage));
                console.log('결제 이력 조회 성공:', response.data.data);
            } else {
                const errorMessage = response.data.message || response.data.error || '알 수 없는 오류가 발생했습니다.';
                console.error('결제 이력 조회 실패:', errorMessage);
                console.error('응답 데이터:', response.data);
                setPaymentHistory([]);
                setPaymentHistoryTotal(0);
            }
        } catch (error) {
            console.error('결제 이력 조회 오류:', error);
            if (error.response) {
                console.error('오류 응답:', error.response.data);
                console.error('오류 상태:', error.response.status);
            }
            setPaymentHistory([]);
            setPaymentHistoryTotal(0);
        } finally {
            setPaymentHistoryLoading(false);
        }
    };

    // 사용자 폼 추가
    const addUserForm = () => {
        const newId = Math.max(...userForms.map(u => u.id)) + 1;
        setUserForms(prev => [...prev, {
            id: newId,
            name: '',
            birthDate: '',
            phone: '',
            orderHistory: null,
            hasSearched: false
        }]);
    };

    // 사용자 폼 제거
    const removeUserForm = (userId) => {
        setUserForms(prev => prev.filter(u => u.id !== userId));
    };

    // 사용자 폼 데이터 변경
    const handleUserFormChange = (userId, field, value) => {
        setUserForms(prev => prev.map(user =>
            user.id === userId ? { ...user, [field]: value } : user
        ));
    };

    // 신규 등록 모달 열기
    const handleNewRegistration = () => {
        setIsModalOpen(true);
    };

    // 모달 닫기
    const handleCloseModal = () => {
        setIsModalOpen(false);
        // 입력 데이터 초기화
        setNewUserData({
            orderNumber: '',
            orderDate: new Date().toISOString().split('T')[0],
            refundDate: '',
            paymentStatus: '',
            recipient: '',
            recipientContact: '',
            packageType: '',
            quantity: '',
            usageStartDate: '',
            smsRecipient: '',
            smsContact: '',
            purchaseDate: new Date().toISOString().split('T')[0],
            remarks: ''
        });
        // 사용자 폼도 초기화
        setUserForms([{
            id: 1,
            name: '',
            birthDate: '',
            phone: '',
            orderHistory: null,
            hasSearched: false
        }]);
    };

    // 신규 사용자 저장
    const handleSaveNewUser = async () => {
        if (!newUserData.orderNumber.trim() || !newUserData.recipient.trim() ||
            !newUserData.recipientContact.trim() || !newUserData.packageType) {
            alert('필수 항목을 모두 입력해주세요.');
            return;
        }

        try {
            const token = localStorage.getItem('token');

            // 패키지 타입을 DB 형식으로 변환
            const packageTypeMap = {
                '효도1': 'HYO_01',
                '효도2': 'HYO_02',
                '개인관리': 'SELF_01',
                '효도1-정기6': 'HYO_01_EXT_6',
                '효도1-정기12': 'HYO_01_EXT_12',
                '효도1-무제한': 'HYO_01_EXT_UNLIMITED',
                '효도2-정기6': 'HYO_02_EXT_6',
                '효도2-정기12': 'HYO_02_EXT_12',
                '효도2-무제한': 'HYO_02_EXT_UNLIMITED'
            };

            const dbPackageType = packageTypeMap[newUserData.packageType];
            if (!dbPackageType) {
                alert('유효하지 않은 패키지 타입입니다.');
                return;
            }

            // 패키지 결제 이력 저장 데이터 준비
            const paymentHistoryData = {
                order_no: newUserData.orderNumber,
                item_order_no: newUserData.orderNumber,
                recipient: newUserData.recipient,
                recipient_mobile: newUserData.recipientContact,
                package_type: dbPackageType,
                status: 'PAID',
                payment_date: newUserData.purchaseDate,
                refund_date: null,
                apply_start_date: newUserData.usageStartDate,
                apply_end_date: getEndDate(newUserData.packageType, newUserData.usageStartDate) === '9999-12-31' ? null : getEndDate(newUserData.packageType, newUserData.usageStartDate),
                description: `신규 사용자 패키지 구매 - ${newUserData.packageType}`,
                etc_mobile: newUserData.smsRecipient || null
            };

            // 패키지 결제 이력 저장 API 호출
            const response = await axios.post(`${API_BASE_URL}/api/package-purchase/save-payment-history`, paymentHistoryData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                alert('신규 사용자 정보가 저장되었습니다.');
                handleCloseModal();

                // 결제 이력 목록 새로고침
                setTimeout(() => {
                    fetchPaymentHistory();
                }, 100);
            } else {
                alert('저장에 실패했습니다: ' + response.data.message);
            }
        } catch (error) {
            console.error('신규 사용자 저장 오류:', error);
            alert('저장 중 오류가 발생했습니다.');
        }
    };

    if (!isSuperAdmin) {
        return null;
    }

    return (
        <OpsLayout
            title="패키지 결제 이력 관리"
            description="주문과 사용자 등록 상태를 함께 봐요."
            actions={(
                <>
                    {/* spec D5: 아직 구현되지 않은 기능이다. 지우지 않고 비활성 + 준비 중으로 남긴다. */}
                    <button className="sds-btn sds-btn--secondary" disabled aria-disabled="true">
                        Excel 양식<Ready />
                    </button>
                    <button className="sds-btn sds-btn--secondary" disabled aria-disabled="true">
                        Excel 업로드<Ready />
                    </button>
                    <button className="sds-btn sds-btn--primary" onClick={handleNewRegistration}>
                        신규 등록
                    </button>
                </>
            )}
        >
            <section className="ops-page__card packages-page__filters">
                <div className="packages-page__grid">
                    <div className="sds-field">
                        <label className="sds-label">패키지</label>
                        <select
                            className="sds-select"
                            value={searchConditions.packageType}
                            onChange={(e) => handleSearchConditionChange('packageType', e.target.value)}
                        >
                            <option value="">전체</option>
                            {packageTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">결제 상태</label>
                        <div className="sds-seg" role="radiogroup" aria-label="결제 상태">
                            {paymentStatusFilters.map(([value, label]) => (
                                <button
                                    key={label}
                                    role="radio"
                                    aria-checked={searchConditions.paymentStatus === value}
                                    className={`sds-seg__item${searchConditions.paymentStatus === value ? ' is-active' : ''}`}
                                    onClick={() => handleSearchConditionChange('paymentStatus', value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">사용자등록상태</label>
                        <div className="sds-seg" role="radiogroup" aria-label="사용자등록상태">
                            {userRegistrationStatusFilters.map(([value, label]) => (
                                <button
                                    key={label}
                                    role="radio"
                                    aria-checked={searchConditions.userRegistrationStatus === value}
                                    className={`sds-seg__item${searchConditions.userRegistrationStatus === value ? ' is-active' : ''}`}
                                    onClick={() => handleSearchConditionChange('userRegistrationStatus', value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">주문일</label>
                        <div className="packages-page__date-range">
                            <input
                                className="sds-input"
                                type="date"
                                value={searchConditions.orderDateStart}
                                onChange={(e) => handleSearchConditionChange('orderDateStart', e.target.value)}
                            />
                            <span>~</span>
                            <input
                                className="sds-input"
                                type="date"
                                value={searchConditions.orderDateEnd}
                                onChange={(e) => handleSearchConditionChange('orderDateEnd', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">수령인</label>
                        <input
                            className="sds-input"
                            value={searchConditions.recipient}
                            onChange={(e) => handleSearchConditionChange('recipient', e.target.value)}
                            placeholder="수령인 이름"
                        />
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">수령인 연락처</label>
                        <input
                            className="sds-input"
                            value={searchConditions.recipientContact}
                            onChange={(e) => handleSearchConditionChange('recipientContact', e.target.value)}
                            placeholder="연락처"
                        />
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">사용자명</label>
                        <input
                            className="sds-input"
                            value={searchConditions.userName}
                            onChange={(e) => handleSearchConditionChange('userName', e.target.value)}
                            placeholder="사용자명"
                        />
                    </div>

                    <div className="sds-field">
                        <label className="sds-label">사용자 연락처</label>
                        <div className="packages-page__search-field">
                            <input
                                className="sds-input"
                                value={searchConditions.userContact}
                                onChange={(e) => handleSearchConditionChange('userContact', e.target.value)}
                                placeholder="연락처"
                            />
                            <button className="sds-btn sds-btn--primary" onClick={fetchPaymentHistory}>조회</button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="ops-page__card">
                <div className="ops-page__card-title">
                    <h2>결제 이력</h2>
                    <small>{paymentHistoryTotal}건</small>
                </div>

                {paymentHistoryLoading ? (
                    <div className="sds-notice">데이터를 불러오는 중...</div>
                ) : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th>주문번호</th>
                                    <th>주문일</th>
                                    <th>결제상태</th>
                                    <th>수령인</th>
                                    <th>사용자</th>
                                    <th>패키지</th>
                                    <th className="sds-table__center">수량</th>
                                    <th>등록상태</th>
                                    <th>사용 기간</th>
                                    <th className="sds-table__center">상세보기</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paymentHistory.length > 0 ? (
                                    paymentHistory.map((record) => (
                                        <tr key={record.id}>
                                            <td title={`ID ${record.id}`}>
                                                <div>{record.itemOrderNo}</div>
                                                {record.description && (
                                                    <small className="packages-page__sub">{record.description}</small>
                                                )}
                                            </td>
                                            <td>{record.orderDate}</td>
                                            <td>
                                                <span className={`sds-badge sds-badge--${opsStatusTone(record.paymentStatus)}`}>
                                                    {record.paymentStatus}
                                                </span>
                                            </td>
                                            <td>
                                                <div>{record.recipient}</div>
                                                <small className="packages-page__sub">{record.recipientContact}</small>
                                            </td>
                                            <td>
                                                <div>{record.userName || '-'}</div>
                                                <small className="packages-page__sub">{record.userContact || '-'}</small>
                                            </td>
                                            <td>{record.packageType}</td>
                                            <td className="sds-table__center">{record.quantity}</td>
                                            <td>
                                                <span className={`sds-badge sds-badge--${opsStatusTone(record.registrationStatus)}`}>
                                                    {record.registrationStatus}
                                                </span>
                                            </td>
                                            <td>{formatUsagePeriod(record.usageStartDate, record.usageEndDate)}</td>
                                            <td className="sds-table__center">
                                                <button className="sds-btn sds-btn--secondary sds-btn--sm" disabled aria-disabled="true">
                                                    상세보기<Ready />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="sds-empty">조회된 결제 이력이 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {paymentHistoryTotalPages > 1 && (
                    <div className="sds-pagination">
                        <button
                            className="sds-pagination__btn"
                            onClick={() => {
                                setPaymentHistoryPage(prev => Math.max(1, prev - 1));
                                setTimeout(() => fetchPaymentHistory(), 0);
                            }}
                            disabled={paymentHistoryPage === 1}
                            aria-label="이전 페이지"
                        >
                            <ChevronLeft size={16} aria-hidden="true" />
                        </button>
                        <span className="packages-page__page-info">{paymentHistoryPage} / {paymentHistoryTotalPages}</span>
                        <button
                            className="sds-pagination__btn"
                            onClick={() => {
                                setPaymentHistoryPage(prev => Math.min(paymentHistoryTotalPages, prev + 1));
                                setTimeout(() => fetchPaymentHistory(), 0);
                            }}
                            disabled={paymentHistoryPage === paymentHistoryTotalPages}
                            aria-label="다음 페이지"
                        >
                            <ChevronRight size={16} aria-hidden="true" />
                        </button>
                    </div>
                )}
            </section>

            {/* 신규 등록 모달 */}
            {isModalOpen && (
                <div className="sds-modal-overlay" onClick={handleCloseModal}>
                    <div
                        className="sds-modal sds-modal--wide"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="package-modal-title"
                    >
                        <div className="sds-modal__head">
                            <h2 id="package-modal-title" className="sds-modal__title">사용자 정보 입력 (신규)</h2>
                            <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={handleCloseModal} aria-label="닫기">
                                <X size={16} aria-hidden="true" />
                            </button>
                        </div>
                        <div className="sds-modal__body">
                            {/* 신규 사용자 정보 입력 */}
                            <div className="packages-page__section">
                                <h3 className="sds-section-title">신규 사용자 정보 입력</h3>
                                <div className="packages-page__form-grid">
                                    <div className="sds-field">
                                        <label className="sds-label">품목별 주문번호</label>
                                        <input
                                            className="sds-input"
                                            value={newUserData.orderNumber}
                                            onChange={(e) => handleNewUserDataChange('orderNumber', e.target.value)}
                                            placeholder="20250821-1289121-01"
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">주문일자</label>
                                        <input
                                            className="sds-input"
                                            type="date"
                                            value={newUserData.orderDate}
                                            onChange={(e) => handleNewUserDataChange('orderDate', e.target.value)}
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">환불일자</label>
                                        <input
                                            className="sds-input"
                                            type="date"
                                            value={newUserData.refundDate}
                                            onChange={(e) => handleNewUserDataChange('refundDate', e.target.value)}
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">결제 상태</label>
                                        <select
                                            className="sds-select"
                                            value={newUserData.paymentStatus}
                                            onChange={(e) => handleNewUserDataChange('paymentStatus', e.target.value)}
                                        >
                                            <option value="">선택하세요</option>
                                            <option value="결제">결제</option>
                                            <option value="환불요청">환불요청</option>
                                            <option value="환불">환불</option>
                                        </select>
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">수령인</label>
                                        <input
                                            className="sds-input"
                                            value={newUserData.recipient}
                                            onChange={(e) => handleNewUserDataChange('recipient', e.target.value)}
                                            placeholder="홍윤철"
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">수령인 연락처</label>
                                        <input
                                            className="sds-input"
                                            value={newUserData.recipientContact}
                                            onChange={(e) => handleNewUserDataChange('recipientContact', e.target.value)}
                                            placeholder="01012341234"
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">패키지</label>
                                        <select
                                            className="sds-select"
                                            value={newUserData.packageType}
                                            onChange={(e) => handleNewUserDataChange('packageType', e.target.value)}
                                        >
                                            <option value="">선택하세요</option>
                                            {packageTypes.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">수량</label>
                                        <input
                                            className="sds-input"
                                            type="number"
                                            value={newUserData.quantity}
                                            onChange={(e) => handleNewUserDataChange('quantity', e.target.value)}
                                            placeholder="3"
                                            min="1"
                                            max="99"
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">사용기간</label>
                                        <input className="sds-input" value={getUsagePeriod(newUserData.packageType)} disabled readOnly />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">SMS 수신자명</label>
                                        <input
                                            className="sds-input"
                                            value={newUserData.smsRecipient}
                                            onChange={(e) => handleNewUserDataChange('smsRecipient', e.target.value)}
                                            placeholder="SMS 수신자명"
                                        />
                                    </div>
                                    <div className="sds-field">
                                        <label className="sds-label">SMS 연락처</label>
                                        <input
                                            className="sds-input"
                                            value={newUserData.smsContact}
                                            onChange={(e) => handleNewUserDataChange('smsContact', e.target.value)}
                                            placeholder="01012345678"
                                        />
                                    </div>
                                    <div className="sds-field packages-page__field-wide">
                                        <label className="sds-label">비고</label>
                                        <textarea
                                            className="sds-textarea"
                                            value={newUserData.remarks}
                                            onChange={(e) => handleNewUserDataChange('remarks', e.target.value)}
                                            placeholder="메모를 입력하세요..."
                                            rows="4"
                                        />
                                    </div>
                                </div>

                                <div className="packages-page__form-actions">
                                    <button className="sds-btn sds-btn--primary" onClick={handleSaveNewUser}>
                                        저장
                                    </button>
                                </div>
                            </div>

                            {/* 사용자 등록 관리 */}
                            <div className="packages-page__section">
                                <h3 className="sds-section-title">사용자 등록 관리</h3>

                                <div className="packages-page__users">
                                    {userForms.map((user, index) => (
                                        <div key={user.id} className="packages-page__user-block">
                                            <div className="packages-page__user-head">
                                                <h4>사용자{index + 1}</h4>
                                                {index > 0 && (
                                                    <button
                                                        className="sds-btn sds-btn--ghost sds-btn--sm"
                                                        onClick={() => removeUserForm(user.id)}
                                                        aria-label={`사용자${index + 1} 제거`}
                                                    >
                                                        <X size={16} aria-hidden="true" />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="packages-page__user-fields">
                                                <div className="sds-field">
                                                    <label className="sds-label">이름</label>
                                                    <input
                                                        className="sds-input"
                                                        value={user.name}
                                                        onChange={(e) => handleUserFormChange(user.id, 'name', e.target.value)}
                                                        placeholder="이름 입력"
                                                    />
                                                </div>
                                                <div className="sds-field">
                                                    <label className="sds-label">생년월일</label>
                                                    <input
                                                        className="sds-input"
                                                        value={user.birthDate}
                                                        onChange={(e) => handleUserFormChange(user.id, 'birthDate', e.target.value)}
                                                        placeholder="생년월일 (YYYYMMDD)"
                                                    />
                                                </div>
                                                <div className="sds-field">
                                                    <label className="sds-label">휴대폰 번호</label>
                                                    <input
                                                        className="sds-input"
                                                        value={user.phone}
                                                        onChange={(e) => handleUserFormChange(user.id, 'phone', e.target.value)}
                                                        placeholder="연락처 입력"
                                                    />
                                                </div>
                                                <div className="sds-field">
                                                    <button className="sds-btn sds-btn--secondary" disabled aria-disabled="true">
                                                        조회<Ready />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 사용자 주문 내역 */}
                                            <div className="packages-page__user-orders sds-table-wrap">
                                                <table className="sds-table">
                                                    <thead>
                                                        <tr>
                                                            <th>품목별 주문번호</th>
                                                            <th>주문일자</th>
                                                            <th>결제상태</th>
                                                            <th>사용상태</th>
                                                            <th>패키지</th>
                                                            <th>기간</th>
                                                            <th>사용 시작일자</th>
                                                            <th>사용 종료일자</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {user.orderHistory && user.orderHistory.length > 0 ? (
                                                            user.orderHistory.map((order, orderIndex) => (
                                                                <tr key={orderIndex}>
                                                                    <td>{order.orderNumber}</td>
                                                                    <td>{order.orderDate}</td>
                                                                    <td>{order.paymentStatus}</td>
                                                                    <td>{order.usageStatus}</td>
                                                                    <td>{order.packageType}</td>
                                                                    <td>{order.period}</td>
                                                                    <td>{order.startDate}</td>
                                                                    <td>{order.endDate}</td>
                                                                </tr>
                                                            ))
                                                        ) : user.hasSearched ? (
                                                            <tr>
                                                                <td colSpan={8} className="sds-empty">해당 사용자가 없습니다.</td>
                                                            </tr>
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={8} className="sds-empty">조회 버튼을 클릭하여 사용자 정보를 검색하세요.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="packages-page__add-user">
                                    <button className="sds-btn sds-btn--secondary" onClick={addUserForm}>
                                        + 사용자 추가
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </OpsLayout>
    );
};

export default PackagePurchaseManagement;
